import { useEffect, useState } from "react";
import { getAuthTokenForRequest } from "@/lib/api";
import { currentScope } from "@/stores/scope";

export type PlayerPhotoKind = "player" | "request";

type CacheEntry = Promise<string | null> | { url: string } | { url: null };

const cache = new Map<string, CacheEntry>();
let generation = 0;
let abortController = new AbortController();

function scopedQuery(): string {
  const scope = currentScope();
  const params = new URLSearchParams();
  if (scope.teamId) params.set("team_id", scope.teamId);
  if (scope.seasonId) params.set("season_id", scope.seasonId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function endpoint(kind: PlayerPhotoKind, id: string): string {
  const encoded = encodeURIComponent(id);
  return kind === "player"
    ? `/api/players/${encoded}/photo${scopedQuery()}`
    : `/api/coach/player-photo-requests/${encoded}/photo${scopedQuery()}`;
}

function key(kind: PlayerPhotoKind, id: string, version?: string | null): string {
  return `${kind}:${id}:${version || "none"}`;
}

export function revokeAllPlayerPhotos(): void {
  generation += 1;
  abortController.abort();
  abortController = new AbortController();
  for (const entry of cache.values()) {
    if (entry && "url" in entry && entry.url) {
      URL.revokeObjectURL(entry.url);
    }
  }
  cache.clear();
}

export function invalidatePlayerPhoto(kind: PlayerPhotoKind, id: string): void {
  for (const [cacheKey, entry] of cache.entries()) {
    if (!cacheKey.startsWith(`${kind}:${id}:`)) continue;
    if (entry && "url" in entry && entry.url) {
      URL.revokeObjectURL(entry.url);
    }
    cache.delete(cacheKey);
  }
}

async function loadPlayerPhoto(
  kind: PlayerPhotoKind,
  id: string,
  version?: string | null,
): Promise<string | null> {
  if (!id || !version) return null;
  const cacheKey = key(kind, id, version);
  const cached = cache.get(cacheKey);
  if (cached !== undefined) {
    if (cached instanceof Promise) return cached;
    return cached.url;
  }

  const startGen = generation;
  const { signal } = abortController;
  const promise = (async (): Promise<string | null> => {
    try {
      const token = getAuthTokenForRequest();
      const resp = await fetch(endpoint(kind, id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal,
      });
      if (generation !== startGen) return null;
      if (!resp.ok) {
        cache.set(cacheKey, { url: null });
        return null;
      }
      const blob = await resp.blob();
      if (generation !== startGen) return null;
      const url = URL.createObjectURL(blob);
      if (generation !== startGen) {
        URL.revokeObjectURL(url);
        return null;
      }
      cache.set(cacheKey, { url });
      return url;
    } catch {
      if (generation === startGen) cache.set(cacheKey, { url: null });
      return null;
    }
  })();

  cache.set(cacheKey, promise);
  return promise;
}

export function usePlayerPhoto(
  kind: PlayerPhotoKind,
  id: string | null | undefined,
  version?: string | null,
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !version) {
      setUrl(null);
      return;
    }
    let active = true;
    setUrl(null);
    loadPlayerPhoto(kind, id, version).then((resolved) => {
      if (active) setUrl(resolved);
    });
    return () => {
      active = false;
    };
  }, [kind, id, version]);

  return url;
}
